import {
  NavigationGuardNext,
  RouteLocationNormalizedLoaded,
  RouteLocationNormalized,
} from 'vue-router';
import DashboardLayout from '@/layouts/DashboardLayout.vue';
import { parse } from 'tldts';

const LandingPage = () => import('../pages/LandingPageV2.vue');
const DynamicInstallation = () =>
  import('../pages/dynamic-installation/DynamicInstallation.vue');
const Report = () => import('../pages/dynamic-report/DynamicReport.vue');
const LoginPage = () => import('../pages/LoginPage.vue');
const DashboardPage = () => import('../pages/dashboard/DashboardPage.vue');
const ReportsPage = () =>
  import('../pages/dashboard/subroutes/ReportsPage.vue');
const ViewAddresses = () =>
  import('../pages/dashboard/subroutes/ViewAddresses.vue');
const CreateBatch = () =>
  import('../pages/dashboard/subroutes/CreateBatch.vue');
const ProvisionedInstallations = () =>
  import('../pages/dashboard/subroutes/ProvisionedInstallations.vue');
const NoProviderRoute = () => import('../pages/NoProviderRoute.vue');
const NotFound = () => import('../pages/NotFound.vue');

export const routes = [
  // Mobile Paths
  {
    path: '/',
    name: 'LandingPage',
    component: LandingPage,
  },
  {
    path: '/:home_id',
    name: 'Installation',
    component: DynamicInstallation,
  },
  {
    path: '/:home_id/report',
    name: 'Report',
    component: Report,
    meta: {
      requiresAuth: true,
    },
  },

  // Desktop Paths
  {
    path: '/login',
    name: 'Login',
    component: LoginPage,
    meta: {
      layout: DashboardLayout,
      title: 'site_names.login',
    },
    // This only has to be done for the login path, because different subdomain -> different local storage
    beforeEnter: (
      _to: RouteLocationNormalized,
      _from: RouteLocationNormalizedLoaded,
      next: NavigationGuardNext,
    ) => {
      if (!isValidHostnameOrSubdomain()) {
        next({ name: 'NoProvider' });
      } else {
        next();
      }
    },
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: DashboardPage,
    meta: {
      layout: DashboardLayout,
      requiresAuth: true,
      title: 'site_names.dashboard',
    },
  },
  {
    path: '/view-addresses',
    name: 'ViewAddresses',
    component: ViewAddresses,
    meta: {
      layout: DashboardLayout,
      parentRoute: 'Dashboard',
      title: 'site_names.addresses',
      requiresAuth: true,
    },
  },
  {
    path: '/reports',
    children: [
      {
        path: '',
        name: 'Reports',
        component: ReportsPage,
        meta: {
          layout: DashboardLayout,
          title: 'site_names.reports',
          parentRoute: 'Dashboard',
          requiresAuth: true,
        },
      },
      {
        path: ':gfap_id',
        name: 'Report',
        component: Report,
        meta: {
          layout: DashboardLayout,
          parentRoute: 'Reports',
          title: 'site_names.report',
          requiresAuth: true,
        },
      },
    ],
  },
  {
    path: '/no-provider',
    name: 'NoProvider',
    component: NoProviderRoute,
    beforeEnter: (
      _to: RouteLocationNormalized,
      _from: RouteLocationNormalizedLoaded,
      next: NavigationGuardNext,
    ) => {
      if (isValidHostnameOrSubdomain()) {
        next({ name: 'Dashboard' });
      } else {
        next();
      }
    },
  },
  {
    path: '/create-batch',
    name: 'CreateBatch',
    component: CreateBatch,
    meta: {
      layout: DashboardLayout,
      parentRoute: 'Dashboard',
      title: 'site_names.create_batch',
      requiresAuth: true,
    },
  },
  {
    path: '/provisioned-installations',
    children: [
      {
        path: '',
        name: 'ProvisionedInstallations',
        component: ProvisionedInstallations,
        meta: {
          layout: DashboardLayout,
          parentRoute: 'Dashboard',
          title: 'site_names.provisioned_installations',
          requiresAuth: true,
        },
      },
      {
        path: ':gfap_id',
        name: 'ProvisionedReport',
        component: Report,
        meta: {
          layout: DashboardLayout,
          parentRoute: 'ProvisionedInstallations',
          title: 'site_names.report',
          requiresAuth: true,
        },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: '404',
    component: NotFound,
  },
];

/**
 * Check if the current hostname is a 2 character subdomain
 * we don't need to verify if the domain actually exists.
 * @returns true if the subdomain is valid, false otherwise
 */
function isValidHostnameOrSubdomain() {
  const hostname = window.location.hostname.toLowerCase();

  // enforce localhost to have a subdomain
  if (hostname.endsWith('.localhost')) {
    const subdomain = hostname.split('.')[0];
    return !!subdomain;
  }

  // Ipv6 loopback address is valid
  if (hostname === '::1') {
    return false;
  }

  const { isIp, subdomain } = parse(hostname);

  // Check if the hostname is a IPv4 address - if it is, we can't use subdomains
  if (isIp) {
    return true;
  }

  return !!subdomain;
}
